/**
 * « Réglages » des briques — refonte UX, chantier 1 ; corrigé le 28/09 après la recette
 * d'Henrico : TOUT réglage ou configuration d'une brique vit DANS l'onglet Réglages (ou
 * Configuration) de la brique, avec son vrai titre — jamais derrière un petit lien.
 *   · Service technique → page Réglages (photo, message de relecture, RELECTEURS gérés ici) ;
 *   · GRC → page Réglages (portée des diffusions, référentiels du conseil municipal) ;
 *   · Comptabilité → onglet « Réglages » DANS la page Comptabilité (ReglagesContenu) ;
 *   · RH → onglet « Paramètres » (roue dentée) DANS la page RH (ReglagesContenu).
 * On a déplacé l'ÉCRAN, jamais la garde serveur ; chaque réglage dit qui peut le modifier :
 *   photo, relecture, relecteurs → service_technique.relecteurs.manage (admin ou manager ST) ;
 *   visibilité des plannings → admin.user.update ; PayFiP → comptable.payfip.manage ;
 *   portée des diffusions → messagerie.diffusion.parametrer ; référentiels → administrateurs.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Shield, User, Settings } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import { GrcNav } from '@/components/GrcNav';
import { ReglageDeclarationCard } from '@/components/admin/ReglageDeclarationCard';
import { MessageRelectureCard } from '@/components/service-technique/MessageRelectureCard';
import { RelecteursPanel } from '@/pages/service-technique/RelecteursPanel';
import { CircuitValidationPanel } from '@/components/service-technique/CircuitValidationPanel';
import { CircuitParModeleSection } from '@/components/service-technique/CircuitParModeleSection';
import { PlanningVisibiliteCard, PayfipConfigCard, DiffusionPorteeCard } from '@/pages/admin/TenantSettingsPage';

export type BriqueReglages = 'service_technique' | 'grc' | 'rh' | 'comptabilite';
type Niveau = 'administrateurs' | 'admin_service' | 'manager_service';

const NIVEAU: Record<Niveau, { libelle: string; icone: ReactNode; cls: string }> = {
  administrateurs: { libelle: 'Administrateurs', icone: <Lock className="h-3.5 w-3.5" aria-hidden="true" />, cls: 'bg-warning/15 text-warning' },
  admin_service: { libelle: 'Admin du service', icone: <Shield className="h-3.5 w-3.5" aria-hidden="true" />, cls: 'bg-tech/15 text-tech-fg' },
  manager_service: { libelle: 'Admin ou manager du service', icone: <User className="h-3.5 w-3.5" aria-hidden="true" />, cls: 'bg-success/15 text-success' },
};

function Reglage({ titre, niveau, children }: { titre: string; niveau: Niveau; children: ReactNode }) {
  const n = NIVEAU[niveau];
  return (
    <section aria-label={titre} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-ink">{titre}</h2>
        <span className={`inline-flex h-6 items-center gap-1.5 rounded-r-pill px-2.5 text-2xs font-medium ${n.cls}`}>{n.icone} Modifiable par : {n.libelle}</span>
      </div>
      {children}
    </section>
  );
}

/** D7 — thèmes d'import (catalogue réel) de chaque brique. L'import reste dans Administration → Données. */
const IMPORTS: Record<BriqueReglages, string[]> = {
  service_technique: ['Matériel et salles', 'Modèles d’actes (arrêtés)'],
  grc: ['Concessions du cimetière', 'Cartes de déchetterie', 'Tiers (annuaire)'],
  rh: ['Catalogue des formations'],
  comptabilite: ['Tiers (annuaire)'],
};

/** Le contenu des réglages d'une brique — réutilisé dans les onglets Compta et RH. */
export function ReglagesContenu({ brique }: { brique: BriqueReglages }) {
  return (
    <div className="space-y-6">
      {brique === 'service_technique' && (<>
        <Reglage titre="Photo des fiches incident" niveau="manager_service"><ReglageDeclarationCard /></Reglage>
        <Reglage titre="Message de relecture par défaut" niveau="manager_service"><MessageRelectureCard /></Reglage>
        <Reglage titre="Relecteurs des arrêtés" niveau="manager_service">
          <p className="text-sm text-ink-muted">
            Un relecteur n’est pas un compte : un nom et une adresse e-mail suffisent (juriste, élu, service de l’État…).
            Il reçoit le projet en PDF et un lien personnel pour donner son avis, ou répond par e-mail.
          </p>
          <RelecteursPanel />
        </Reglage>
        <Reglage titre="Circuit de validation des arrêtés" niveau="manager_service"><CircuitValidationPanel /></Reglage>
        <Reglage titre="Circuit par modèle d’arrêté (cas sensibles)" niveau="manager_service"><CircuitParModeleSection /></Reglage>
      </>)}
      {brique === 'grc' && (<>
        <Reglage titre="Portée des diffusions" niveau="administrateurs"><DiffusionPorteeCard /></Reglage>
        <Reglage titre="Référentiels du conseil municipal" niveau="administrateurs">
          <div className="rounded-r-xl border border-border bg-surface/55 p-4">
            <p className="text-sm text-ink">Les matières, les sujets et les pièces attendues utilisés par les délibérations.</p>
            <Link to="/admin/deliberations" className="mt-3 inline-flex h-10 items-center rounded-r-md bg-grc px-4 text-sm font-medium text-white hover:opacity-90">
              Gérer les référentiels du conseil
            </Link>
          </div>
        </Reglage>
      </>)}
      {brique === 'rh' && (
        <Reglage titre="Visibilité des plannings pour les agents" niveau="administrateurs"><PlanningVisibiliteCard /></Reglage>
      )}
      {brique === 'comptabilite' && (
        <Reglage titre="Paiement en ligne (PayFiP) et facture électronique" niveau="admin_service"><PayfipConfigCard /></Reglage>
      )}
      <Reglage titre="Importer des données du service" niveau="administrateurs">
        <p className="text-sm text-ink-muted">
          À partir d’un fichier Excel, avec un aperçu avant l’import : {IMPORTS[brique].join(', ')}.{' '}
          <Link className="text-accent underline" to="/admin/donnees">Ouvrir l’import</Link>
        </p>
      </Reglage>
    </div>
  );
}

/** Pages autonomes : service technique et GRC (les autres briques ont l'onglet dans leur page). */
export function ReglagesBriquePage({ brique }: { brique: 'service_technique' | 'grc' }) {
  const t = brique === 'service_technique'
    ? { module: 'Service technique', accent: 'tech' as const, titre: 'Réglages du service technique' }
    : { module: 'Relation citoyens', accent: 'grc' as const, titre: 'Réglages de la relation citoyens' };
  return (
    <div className="min-h-screen">
      <AppHeader module={t.module} accent={t.accent} />
      {brique === 'service_technique' && <ServiceTechniqueNav active="reglages" />}
      {brique === 'grc' && <div className="mx-auto max-w-6xl px-4 pt-4"><GrcNav active="reglages" /></div>}
      <main id="main-content" className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-semibold text-ink"><Settings className="h-5 w-5" aria-hidden="true" /> {t.titre}</h1>
          <p className="mt-1 text-sm text-ink-muted">Les réglages de ce service, au même endroit. Chacun indique qui peut le modifier.</p>
        </div>
        <ReglagesContenu brique={brique} />
      </main>
    </div>
  );
}
